import { useState, useEffect, useRef } from "react";
import { Icon } from "@core/components";
import { cn } from "@core/utils/cn";
import { useTranslate } from "@core/hooks";
import type { PdfPageMetrics } from "@core/types";
import type {
  ShapeAnnotation,
  ShapeKind,
  RGBA,
  AnnotationType,
  DragState,
  ResizeHandle,
  ShapeGeometry,
} from "@modules/docs/annotations/types/TAnnotation";
import { PAGE_GAP, PAGE_TOP_PADDING } from "@core/types";
import {
  MIN_BOX_SIZE,
  toNormRect,
  toNormCircleFromRect,
  toNormPolylineFromPoints,
  fromNormPolylineToPoints,
  geometryToRect,
} from "@modules/docs/annotations/utils/shape";
import { buildRevisionCloudPathData } from "@modules/docs/annotations/utils/cloud";

// ─── Constants ────────────────────────────────────────────────────────────────

const ARROW_HEAD_LEN = 12;
const ARROW_STROKE_WIDTH = 2;

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function pageTopPx(pageIndex: number, pageHeightScaled: number): number {
  return pageIndex * (pageHeightScaled + PAGE_GAP) + PAGE_TOP_PADDING;
}

function getPageIndexFromY(
  absY: number,
  pageHeightScaled: number,
  totalPages: number,
): number {
  return Math.max(
    0,
    Math.min(
      totalPages - 1,
      Math.floor((absY - PAGE_TOP_PADDING) / (pageHeightScaled + PAGE_GAP)),
    ),
  );
}

function withAlpha(rgba: RGBA, alpha: number): string {
  return rgba.replace(/,\s*[\d.]+\)$/, `, ${alpha})`);
}

// ─── Props ────────────────────────────────────────────────────────────────────

export type ShapeOverlayProps = {
  pageMetrics: PdfPageMetrics; // guaranteed non-null — parent renders conditionally
  totalPages: number;
  scale: number;
  shapes: ShapeAnnotation[];
  onShapesChange: (shapes: ShapeAnnotation[]) => void;
  selectedShapeId: string | null;
  onSelectShape: (id: string | null) => void;
  activeTool: AnnotationType;
  shapeKind: ShapeKind;
  color: RGBA;
  canAnnotate: boolean;
  onRequestComment: (annotationId: string) => void;
};

// ─── Draft shape state ────────────────────────────────────────────────────────

type DraftShape = {
  startX: number; // overlay-absolute coords (scaled px)
  startY: number;
  currentX: number;
  currentY: number;
} | null;

// ─── Component ────────────────────────────────────────────────────────────────

export const ShapeOverlay = ({
  pageMetrics,
  totalPages,
  scale,
  shapes,
  onShapesChange,
  selectedShapeId,
  onSelectShape,
  activeTool,
  shapeKind,
  color,
  canAnnotate,
  onRequestComment,
}: ShapeOverlayProps) => {
  const { t } = useTranslate("documents");
  const overlayRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState<DraftShape>(null);
  const dragging = useRef<DragState | null>(null);
  // Incrementing this forces a re-render so dragging.current.currentRect is reflected.
  const [, setRenderTick] = useState(0);

  const { originalPageWidth: pageWidth, originalPageHeight: pageHeight } =
    pageMetrics;
  const ph = pageHeight * scale; // scaled page height
  const overlayHeight = totalPages * (ph + PAGE_GAP) + PAGE_TOP_PADDING * 2;
  const isShapeTool = activeTool === "shape";

  // ── Page centering offset ──────────────────────────────────────────────────
  // PDF pages are centered inside the scroll container (items-center).
  // pageOffsetX is the horizontal distance from the overlay's left edge to the
  // page's left edge. Shapes are stored in page-local coordinates, so we must
  // subtract this offset when drawing and add it when rendering.

  const pageWidthScaled = pageWidth * scale;
  const [pageOffsetX, setPageOffsetX] = useState(0);

  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    const update = () => {
      setPageOffsetX(Math.max(0, (el.clientWidth - pageWidthScaled) / 2));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [pageWidthScaled]);

  // ── Coordinate helper ──────────────────────────────────────────────────────

  const getOverlayPos = (
    e: { clientX: number; clientY: number },
    overrideRect?: DOMRect,
  ): { x: number; y: number } => {
    const rect =
      overrideRect ??
      overlayRef.current?.getBoundingClientRect() ??
      new DOMRect();
    // rect.top goes negative as user scrolls — so clientY - rect.top gives absolute
    // y within the overlay's content space (same as if overlay was at scroll origin).
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  // ── Delete key handler ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!selectedShapeId || !canAnnotate) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        onShapesChange(shapes.filter((s) => s.id !== selectedShapeId));
        onSelectShape(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedShapeId, canAnnotate, shapes, onShapesChange, onSelectShape]);

  // ── Drawing — overlay mouse handlers ──────────────────────────────────────

  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!canAnnotate || !isShapeTool) return;
    const { x, y } = getOverlayPos(e);
    setDraft({ startX: x, startY: y, currentX: x, currentY: y });
  };

  const handleOverlayMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!draft) return;
    const { x, y } = getOverlayPos(e);
    setDraft((d) => (d ? { ...d, currentX: x, currentY: y } : null));
  };

  const handleOverlayMouseUp = () => {
    if (!draft || !canAnnotate) {
      setDraft(null);
      return;
    }

    const pageIndex = getPageIndexFromY(draft.startY, ph, totalPages);
    const pageTop = pageTopPx(pageIndex, ph);

    // Convert overlay absolute coords to unscaled page-local coords
    const toPageLocal = (overlayX: number, overlayY: number) => ({
      x: (overlayX - pageOffsetX) / scale,
      y: (overlayY - pageTop) / scale,
    });

    const p1 = toPageLocal(draft.startX, draft.startY);
    const p2 = toPageLocal(draft.currentX, draft.currentY);

    const localW = Math.abs(p2.x - p1.x);
    const localH = Math.abs(p2.y - p1.y);
    const dist = Math.sqrt(localW * localW + localH * localH);

    const isTooSmall =
      shapeKind === "arrow"
        ? dist < MIN_BOX_SIZE
        : localW < MIN_BOX_SIZE || localH < MIN_BOX_SIZE;

    if (!isTooSmall) {
      let geometry: ShapeGeometry;

      if (shapeKind === "arrow") {
        geometry = toNormPolylineFromPoints([p1, p2], pageWidth, pageHeight);
      } else if (shapeKind === "circle") {
        geometry = toNormCircleFromRect(
          Math.min(p1.x, p2.x),
          Math.min(p1.y, p2.y),
          Math.max(p1.x, p2.x),
          Math.max(p1.y, p2.y),
          pageWidth,
          pageHeight,
        );
      } else {
        // rectangle and cloud both stored as NormRect
        geometry = toNormRect(
          Math.min(p1.x, p2.x),
          Math.min(p1.y, p2.y),
          Math.max(p1.x, p2.x),
          Math.max(p1.y, p2.y),
          pageWidth,
          pageHeight,
        );
      }

      const newShape: ShapeAnnotation = {
        id: crypto.randomUUID(),
        type: "shape",
        kind: shapeKind,
        color,
        source: "local",
        areas: [{ page: pageIndex, position: geometry }],
      };

      onShapesChange([...shapes, newShape]);
      onSelectShape(newShape.id);
    }

    setDraft(null);
  };

  // ── Drag / resize — shape area mouse handlers ─────────────────────────────

  const handleShapeAreaMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    shape: ShapeAnnotation,
    areaIndex: number,
    handle?: ResizeHandle,
  ) => {
    if (!canAnnotate) return;
    e.stopPropagation();

    const area = shape.areas[areaIndex];
    const rect = geometryToRect(area.position, pageWidth, pageHeight);
    const overlayRect =
      overlayRef.current?.getBoundingClientRect() ?? new DOMRect();
    const { x: mouseX, y: mouseY } = getOverlayPos(e, overlayRect);

    dragging.current = {
      id: shape.id,
      areaIndex,
      mode: handle ? "resize" : "move",
      handle,
      startMouseX: mouseX,
      startMouseY: mouseY,
      startRect: rect,
      currentRect: { ...rect },
      overlayRect,
    };

    onSelectShape(shape.id);

    // Window listeners for reliable drag even when mouse leaves overlay
    const handleWindowMouseMove = (ev: MouseEvent) => {
      const ds = dragging.current;
      if (!ds) return;

      const { x: mx, y: my } = getOverlayPos(ev, ds.overlayRect);
      const dX = (mx - ds.startMouseX) / scale;
      const dY = (my - ds.startMouseY) / scale;

      const { startRect, mode, handle: h } = ds;
      let { x, y, w, h: height } = startRect;

      if (mode === "move") {
        x = startRect.x + dX;
        y = startRect.y + dY;
      } else {
        // resize
        if (h === "se") {
          w = Math.max(MIN_BOX_SIZE, startRect.w + dX);
          height = Math.max(MIN_BOX_SIZE, startRect.h + dY);
        } else if (h === "sw") {
          const newW = Math.max(MIN_BOX_SIZE, startRect.w - dX);
          x = startRect.x + startRect.w - newW;
          w = newW;
          height = Math.max(MIN_BOX_SIZE, startRect.h + dY);
        } else if (h === "ne") {
          w = Math.max(MIN_BOX_SIZE, startRect.w + dX);
          const newH = Math.max(MIN_BOX_SIZE, startRect.h - dY);
          y = startRect.y + startRect.h - newH;
          height = newH;
        } else if (h === "nw") {
          const newW = Math.max(MIN_BOX_SIZE, startRect.w - dX);
          x = startRect.x + startRect.w - newW;
          w = newW;
          const newH = Math.max(MIN_BOX_SIZE, startRect.h - dY);
          y = startRect.y + startRect.h - newH;
          height = newH;
        }
      }

      if (dragging?.current) {
        dragging.current.currentRect = { x, y, w, h: height };
      }
      setRenderTick((t) => t + 1);
    };

    const handleWindowMouseUp = () => {
      const ds = dragging.current;
      if (!ds) return;

      // Commit the new geometry
      const targetShape = shapes.find((s) => s.id === ds.id);
      if (targetShape) {
        const area_ = targetShape.areas[ds.areaIndex];
        const { x, y, w, h: height } = ds.currentRect;

        let newPosition: ShapeGeometry;

        if ("points" in area_.position) {
          // Arrow — translate all points by the drag delta
          const origPts = fromNormPolylineToPoints(
            area_.position,
            pageWidth,
            pageHeight,
          );
          const dx = x - ds.startRect.x;
          const dy = y - ds.startRect.y;
          newPosition = toNormPolylineFromPoints(
            origPts.map((p) => ({ x: p.x + dx, y: p.y + dy })),
            pageWidth,
            pageHeight,
          );
        } else if ("cx" in area_.position) {
          newPosition = toNormCircleFromRect(
            x,
            y,
            x + w,
            y + height,
            pageWidth,
            pageHeight,
          );
        } else {
          newPosition = toNormRect(
            x,
            y,
            x + w,
            y + height,
            pageWidth,
            pageHeight,
          );
        }

        onShapesChange(
          shapes.map((s) => {
            if (s.id !== ds.id) return s;
            return {
              ...s,
              areas: s.areas.map((a, i) =>
                i === ds.areaIndex ? { ...a, position: newPosition } : a,
              ),
            };
          }),
        );
      }

      dragging.current = null;
      setRenderTick((t) => t + 1);
      window.removeEventListener("mousemove", handleWindowMouseMove);
      window.removeEventListener("mouseup", handleWindowMouseUp);
    };

    window.addEventListener("mousemove", handleWindowMouseMove);
    window.addEventListener("mouseup", handleWindowMouseUp);
  };

  // ── Delete shape handler ───────────────────────────────────────────────────

  const handleDeleteShape = (id: string) => {
    onShapesChange(shapes.filter((s) => s.id !== id));
    onSelectShape(null);
  };

  // ── Draft bounding box (overlay-absolute coords, scaled) ──────────────────

  const draftBox = draft
    ? {
        x: Math.min(draft.startX, draft.currentX),
        y: Math.min(draft.startY, draft.currentY),
        w: Math.abs(draft.currentX - draft.startX),
        h: Math.abs(draft.currentY - draft.startY),
      }
    : null;

  // ── Resize handle config ───────────────────────────────────────────────────

  type HandleConfig = {
    handle: ResizeHandle;
    style: React.CSSProperties;
  };

  const RESIZE_HANDLES: HandleConfig[] = [
    { handle: "nw", style: { top: -4, left: -4, cursor: "nw-resize" } },
    { handle: "ne", style: { top: -4, right: -4, cursor: "ne-resize" } },
    { handle: "sw", style: { bottom: -4, left: -4, cursor: "sw-resize" } },
    { handle: "se", style: { bottom: -4, right: -4, cursor: "se-resize" } },
  ];

  const commonHandleStyle: React.CSSProperties = {
    position: "absolute",
    width: 8,
    height: 8,
    backgroundColor: "#fff",
    border: "1.5px solid #3b82f6",
    borderRadius: 2,
    zIndex: 1,
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      ref={overlayRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: overlayHeight,
        zIndex: 10,
        pointerEvents: isShapeTool ? "auto" : "none",
        cursor: isShapeTool && canAnnotate ? "crosshair" : "default",
      }}
      onMouseDown={handleOverlayMouseDown}
      onMouseMove={handleOverlayMouseMove}
      onMouseUp={handleOverlayMouseUp}
      onClick={() => onSelectShape(null)}
    >
      {/* ── Rendered shapes ── */}
      {shapes.flatMap((shape) =>
        shape.areas.map((area, areaIndex) => {
          const ds = dragging.current;
          const isDragging =
            ds !== null && ds.id === shape.id && ds.areaIndex === areaIndex;

          // Use live rect while dragging, otherwise compute from stored geometry
          const baseRect = geometryToRect(area.position, pageWidth, pageHeight);
          const rect = isDragging && ds ? ds.currentRect : baseRect;

          const areaPageTop = pageTopPx(area.page, ph);
          const absTop = areaPageTop + rect.y * scale;
          const absLeft = rect.x * scale + pageOffsetX;
          const absW = rect.w * scale;
          const absH = rect.h * scale;

          const isSelected = selectedShapeId === shape.id;

          const containerStyle: React.CSSProperties = {
            position: "absolute",
            top: absTop,
            left: absLeft,
            width: absW,
            height: absH,
            boxSizing: "border-box",
            cursor: canAnnotate ? "move" : "default",
          };

          const selectionRing: React.CSSProperties = isSelected
            ? { outline: "2px solid #3b82f6", outlineOffset: "2px" }
            : {};

          const key = `${shape.id}-${areaIndex}`;

          // ── Shape-specific rendering ──

          if (shape.kind === "arrow") {
            // Arrow uses SVG; bounding box is the container
            if (!("points" in area.position)) return null;

            const pts = isDragging
              ? (() => {
                  const origPts = fromNormPolylineToPoints(
                    area.position,
                    pageWidth,
                    pageHeight,
                  );
                  if (origPts.length < 2) return origPts;
                  const dx = rect.x - baseRect.x;
                  const dy = rect.y - baseRect.y;
                  return origPts.map((p) => ({ x: p.x + dx, y: p.y + dy }));
                })()
              : fromNormPolylineToPoints(area.position, pageWidth, pageHeight);

            if (pts.length < 2) return null;

            const [p1, p2] = pts;
            const svgPad = ARROW_HEAD_LEN + 4;
            const svgMinX = Math.min(p1.x, p2.x) * scale - svgPad;
            const svgMinY = Math.min(p1.y, p2.y) * scale - svgPad;
            const svgW = Math.abs(p2.x - p1.x) * scale + 2 * svgPad;
            const svgH = Math.abs(p2.y - p1.y) * scale + 2 * svgPad;

            const markerId = `arrow-head-${shape.id}`;

            return (
              <div
                key={key}
                style={{
                  position: "absolute",
                  top: areaPageTop + svgMinY,
                  left: svgMinX + pageOffsetX,
                  width: svgW,
                  height: svgH,
                  cursor: canAnnotate ? "move" : "default",
                  ...selectionRing,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectShape(shape.id);
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  if (shape.source === "backend") onRequestComment(shape.id);
                }}
                onMouseDown={(e) =>
                  handleShapeAreaMouseDown(e, shape, areaIndex)
                }
              >
                <svg
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    overflow: "visible",
                    pointerEvents: "none",
                  }}
                >
                  <defs>
                    <marker
                      id={markerId}
                      markerWidth="10"
                      markerHeight="7"
                      refX="9"
                      refY="3.5"
                      orient="auto"
                    >
                      <polygon points="0 0, 10 3.5, 0 7" fill={shape.color} />
                    </marker>
                  </defs>
                  <line
                    x1={p1.x * scale - svgMinX}
                    y1={p1.y * scale - svgMinY}
                    x2={p2.x * scale - svgMinX}
                    y2={p2.y * scale - svgMinY}
                    stroke={shape.color}
                    strokeWidth={ARROW_STROKE_WIDTH}
                    markerEnd={`url(#${markerId})`}
                  />
                </svg>

                {/* Floating menu */}
                {isSelected && (
                  <FloatingMenu
                    shape={shape}
                    canAnnotate={canAnnotate}
                    onRequestComment={onRequestComment}
                    onDelete={handleDeleteShape}
                    t={t}
                  />
                )}
              </div>
            );
          }

          if (shape.kind === "cloud") {
            const cloudPath = buildRevisionCloudPathData(absW, absH, {
              targetDiameter: Math.max(12, Math.min(28, Math.min(absW, absH) / 5)),
              minBumpsPerSide: 3,
            });

            return (
              <div
                key={key}
                style={{ ...containerStyle, ...selectionRing }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectShape(shape.id);
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  if (shape.source === "backend") onRequestComment(shape.id);
                }}
                onMouseDown={(e) =>
                  handleShapeAreaMouseDown(e, shape, areaIndex)
                }
              >
                <svg
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    overflow: "visible",
                    pointerEvents: "none",
                  }}
                  viewBox={`0 0 ${absW} ${absH}`}
                >
                  <path
                    d={cloudPath}
                    fill={withAlpha(shape.color, 0.08)}
                    stroke={shape.color}
                    strokeWidth={2}
                  />
                </svg>

                {isSelected &&
                  canAnnotate &&
                  RESIZE_HANDLES.map(({ handle, style }) => (
                    <div
                      key={handle}
                      style={{ ...commonHandleStyle, ...style }}
                      onMouseDown={(e) =>
                        handleShapeAreaMouseDown(e, shape, areaIndex, handle)
                      }
                    />
                  ))}

                {isSelected && (
                  <FloatingMenu
                    shape={shape}
                    canAnnotate={canAnnotate}
                    onRequestComment={onRequestComment}
                    onDelete={handleDeleteShape}
                    t={t}
                  />
                )}
              </div>
            );
          }

          // Rectangle and circle — rendered as styled divs
          const borderRadius = shape.kind === "circle" ? "9999px" : "0";

          return (
            <div
              key={key}
              style={{
                ...containerStyle,
                ...selectionRing,
                border: `2px solid ${shape.color}`,
                backgroundColor: isSelected
                  ? withAlpha(shape.color, 0.06)
                  : "transparent",
                borderRadius,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onSelectShape(shape.id);
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (shape.source === "backend") onRequestComment(shape.id);
              }}
              onMouseDown={(e) => handleShapeAreaMouseDown(e, shape, areaIndex)}
            >
              {isSelected &&
                shape.kind !== "circle" &&
                canAnnotate &&
                RESIZE_HANDLES.map(({ handle, style }) => (
                  <div
                    key={handle}
                    style={{ ...commonHandleStyle, ...style }}
                    onMouseDown={(e) =>
                      handleShapeAreaMouseDown(e, shape, areaIndex, handle)
                    }
                  />
                ))}

              {/* Circle resize handles — allow on circle too */}
              {isSelected &&
                shape.kind === "circle" &&
                canAnnotate &&
                RESIZE_HANDLES.map(({ handle, style }) => (
                  <div
                    key={handle}
                    style={{ ...commonHandleStyle, ...style }}
                    onMouseDown={(e) =>
                      handleShapeAreaMouseDown(e, shape, areaIndex, handle)
                    }
                  />
                ))}

              {isSelected && (
                <FloatingMenu
                  shape={shape}
                  canAnnotate={canAnnotate}
                  onRequestComment={onRequestComment}
                  onDelete={handleDeleteShape}
                  t={t}
                />
              )}
            </div>
          );
        }),
      )}

      {/* ── Draft shape preview ── */}
      {draftBox && draftBox.w > 0 && draftBox.h > 0 && (
        <DraftPreview
          box={draftBox}
          shapeKind={shapeKind}
          color={color}
          draft={draft}
          ph={ph}
        />
      )}
    </div>
  );
};

// ─── FloatingMenu ─────────────────────────────────────────────────────────────

type FloatingMenuProps = {
  shape: ShapeAnnotation;
  canAnnotate: boolean;
  onRequestComment: (id: string) => void;
  onDelete: (id: string) => void;
  t: (key: string) => string;
};

const FloatingMenu = ({
  shape,
  canAnnotate,
  onRequestComment,
  onDelete,
  t,
}: FloatingMenuProps) => (
  <div
    className="absolute -right-16 top-5 flex flex-col gap-1 bg-white rounded-lg border border-neutral-200 shadow-sm p-1"
    style={{ zIndex: 20 }}
    onClick={(e) => e.stopPropagation()}
    onMouseDown={(e) => e.stopPropagation()}
  >
    <button
      type="button"
      disabled={shape.source === "local"}
      title={shape.source === "local" ? t("annotations.save-first-to-comment") : undefined}
      className={cn(
        "w-7 h-7 flex items-center justify-center rounded hover:bg-neutral-100 text-neutral-500",
        shape.source === "local" && "opacity-40 cursor-not-allowed",
      )}
      onClick={(e) => {
        e.stopPropagation();
        if (shape.source === "backend") onRequestComment(shape.id);
      }}
    >
      <Icon name="message-circle" className="w-4 h-4" strokeWidth={1.6} />
    </button>
    {canAnnotate && (
      <button
        type="button"
        className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-50 text-neutral-500 hover:text-accent-red-600"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(shape.id);
        }}
      >
        <Icon name="trash-2" className="w-4 h-4" strokeWidth={1.6} />
      </button>
    )}
  </div>
);

// ─── DraftPreview ─────────────────────────────────────────────────────────────

type DraftPreviewProps = {
  box: { x: number; y: number; w: number; h: number };
  shapeKind: ShapeKind;
  color: RGBA;
  draft: DraftShape;
  ph: number; // scaled page height
};

const DraftPreview = ({
  box,
  shapeKind,
  color,
  draft,
  ph,
}: DraftPreviewProps) => {
  // For arrow: show a line from start to current (not bounding box)
  if (shapeKind === "arrow" && draft) {
    const svgPad = ARROW_HEAD_LEN + 4;
    const svgMinX = box.x - svgPad;
    const svgMinY = box.y - svgPad;
    const svgW = box.w + 2 * svgPad;
    const svgH = box.h + 2 * svgPad;

    return (
      <div
        style={{
          position: "absolute",
          top: svgMinY,
          left: svgMinX,
          width: svgW,
          height: svgH,
          pointerEvents: "none",
        }}
      >
        <svg
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            overflow: "visible",
          }}
        >
          <line
            x1={draft.startX - svgMinX}
            y1={draft.startY - svgMinY}
            x2={draft.currentX - svgMinX}
            y2={draft.currentY - svgMinY}
            stroke={color}
            strokeWidth={ARROW_STROKE_WIDTH}
            strokeDasharray="6 3"
          />
        </svg>
      </div>
    );
  }

  // For cloud: show dashed rect (simplified draft — full cloud on commit)
  const borderRadius = shapeKind === "circle" ? "9999px" : "0";

  // Offset pageTopPx is already baked into box.y (it's in overlay-absolute coords)
  void ph; // used indirectly via box coords already accounting for page offset

  return (
    <div
      style={{
        position: "absolute",
        top: box.y,
        left: box.x,
        width: box.w,
        height: box.h,
        border: `2px dashed ${color}`,
        borderRadius,
        pointerEvents: "none",
        boxSizing: "border-box",
      }}
    />
  );
};
